export const MAX_BUSINESS_SLOT_TYPE = 'maxSlot'
export const SMS_CHANNEL_SLOT_TYPE = 'smsChannelSlot'

export type ChannelSlotLike = {
    id?: string
    channelConnectorId?: string | null
    isTariff?: boolean | number | string
    type?: string | null
}

export function isTariffSlot(slot: { isTariff?: boolean | number | string } | null | undefined): boolean {
    const value = slot?.isTariff
    return value === true || value === 1 || value === '1' || value === 'true'
}

export function isMaxBusinessSlotType(slot?: { type?: string | null } | null): boolean {
    return String(slot?.type || '') === MAX_BUSINESS_SLOT_TYPE
}

export function isSmsChannelSlotType(slot?: { type?: string | null } | null): boolean {
    return String(slot?.type || '') === SMS_CHANNEL_SLOT_TYPE
}

function isTrialFlag(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true'
}

/** Подарочный SMS-слот промо: тип smsChannelSlot и isTrial. */
export function isSmsPromoGiftSlot(
    slot?: { type?: string | null; isTrial?: unknown } | null
): boolean {
    return isSmsChannelSlotType(slot) && isTrialFlag(slot?.isTrial)
}

export function findFreeSmsPromoSlotId(
    slots: Array<{
        id?: string | number | null
        channelConnectorId?: string | null
        type?: string | null
        isTrial?: unknown
    }> | null | undefined
): string | null {
    const free = (slots || []).find((slot) => {
        const bound = slot?.channelConnectorId
        const unbound = bound == null || bound === ''
        return isSmsPromoGiftSlot(slot) && unbound && slot?.id != null
    })
    return free?.id != null ? String(free.id) : null
}

function sameSlotRef(left: unknown, right: unknown): boolean {
    if (left == null || right == null || left === '' || right === '') return false
    return String(left) === String(right)
}

/** SMS slot created for this promo: same subscription, then same install, then the trial slot. */
export function findSmsPromoRenewalSlot<
    T extends {
        type?: string | null
        isTrial?: unknown
        subscriptionId?: string | number | null
        installId?: string | number | null
        renewalMode?: string | null
    }
>(
    slots: T[] | null | undefined,
    promo: { subscriptionId?: string | number | null; installId?: string | number | null } | null | undefined
): T | null {
    const smsSlots = (slots || []).filter((slot) => isSmsChannelSlotType(slot))
    if (!smsSlots.length || !promo) return null
    return (
        smsSlots.find((slot) => sameSlotRef(slot.subscriptionId, promo.subscriptionId)) ||
        smsSlots.find((slot) => sameSlotRef(slot.installId, promo.installId)) ||
        smsSlots.find((slot) => isSmsPromoGiftSlot(slot)) ||
        null
    )
}

function isFreeSlot(slot: ChannelSlotLike | null | undefined): boolean {
    const bound = slot?.channelConnectorId
    return bound == null || bound === ''
}

export function getFreeSlotsInFillOrder<T extends ChannelSlotLike>(slots: T[]): T[] {
    const freeSlots = slots.filter(
        (slot) =>
            isFreeSlot(slot) && !isMaxBusinessSlotType(slot) && !isSmsChannelSlotType(slot)
    )
    const tariffSlots = freeSlots.filter(isTariffSlot)
    const additionalSlots = freeSlots.filter((slot) => !isTariffSlot(slot))
    return [...tariffSlots, ...additionalSlots]
}

export function getFreeMaxBusinessSlots<T extends ChannelSlotLike>(slots: T[]): T[] {
    return slots.filter((slot) => isMaxBusinessSlotType(slot) && isFreeSlot(slot))
}

export function getFirstFreeSlotId(
    slots: ChannelSlotLike[] | null | undefined,
): string | null {
    const [firstFreeSlot] = getFreeSlotsInFillOrder(slots || [])
    return firstFreeSlot?.id ? String(firstFreeSlot.id) : null
}

export function getFirstFreeMaxSlotId(
    slots: ChannelSlotLike[] | null | undefined,
): string | null {
    const [firstFreeSlot] = getFreeMaxBusinessSlots(slots || [])
    return firstFreeSlot?.id ? String(firstFreeSlot.id) : null
}

export function getFirstAvailableSlot(
    slots: Array<ChannelSlotLike & { channelDeletedDate?: string | null }> | null | undefined,
) {
    const [firstFreeSlot] = getFreeSlotsInFillOrder(slots || [])
    return firstFreeSlot || null
}

export function hasAvailableChannelSlots(
    slots: ChannelSlotLike[] | null | undefined,
): boolean {
    return getFreeSlotsInFillOrder(slots || []).length > 0
}

export function buildInactiveSlotConnectItem(slot: {
    id?: string
    isTariff?: boolean | number | string
    channelDeletedDate?: string | null
}) {
    return {
        isActive: false,
        _slotId: slot.id,
        _channelDeletedDate: slot.channelDeletedDate || null,
        isAdditional: !isTariffSlot(slot),
    }
}
